import { useQuery } from '@apollo/client/react';
import { uniqBy } from 'lodash';
import { useEffect, useState } from 'react';

import { Range } from '@calo/types';

import { useQueryErrorToast } from './useQueryErrorToast';
import { ComponentBatch } from '../libs';
import { GraphQLClient, LIST_COMPONENT_BATCHES_QUERY, SUBSCRIBE_TO_COMPONENT_BATCH_CHANGES_SUBSCRIPTION } from '../libs/graphQL';

type ListComponentBatchesData = { listComponentBatches: { data: ComponentBatch[]; total?: number } };

const useListComponentBatchesQuery = (kitchen: string, date: Range, shift?: string) => {
  const [batches, setBatches] = useState<ComponentBatch[]>([]);
  const { data, error, loading, refetch, subscribeToMore } = useQuery<ListComponentBatchesData>(LIST_COMPONENT_BATCHES_QUERY, {
    variables: { kitchen, date, shift }
  });

  useEffect(() => {
    if (data?.listComponentBatches?.data) {
      setBatches(data.listComponentBatches.data);
    }
  }, [data]);

  useQueryErrorToast(error);

  useEffect(
    () =>
      subscribeToMore<{ subscribeToComponentBatchChanges: ComponentBatch }>({
        document: SUBSCRIBE_TO_COMPONENT_BATCH_CHANGES_SUBSCRIPTION,
        variables: { kitchen },
        updateQuery: (previous, { subscriptionData }) => {
          const prev = previous as ListComponentBatchesData;
          if (!subscriptionData.data) return prev;
          const prevData = Array.isArray(prev?.listComponentBatches?.data) ? prev.listComponentBatches.data : [];
          const subscribeToComponentBatches = subscriptionData.data.subscribeToComponentBatchChanges;

          return {
            ...prev,
            listComponentBatches: {
              ...prev.listComponentBatches,
              data: uniqBy([subscribeToComponentBatches, ...prevData], 'id')
            }
          };
        }
      }),
    [kitchen, subscribeToMore]
  );

  const clearData = () => {
    setBatches([]);
    void GraphQLClient.clearStore();
  };

  return {
    componentBatchesLoading: loading,
    batches,
    refetchBatches: refetch,
    clearBatchesData: clearData
  };
};

export default useListComponentBatchesQuery;
